import type { AxiosError } from 'axios';
import { authClient } from './client';
import type { AuditChange, AuditLogItem, AuditLogPage, AuditOrder } from '../types/auditLog';

export interface AuditLogQuery {
  page?: number;
  pageSize?: number;
  order?: AuditOrder;
}

export type AuditLogResult = { ok: true; data: AuditLogPage } | { ok: false; error: string };

// ─── Parsers ──────────────────────────────────────────────────────────────────

function parseChange(raw: unknown): AuditChange | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const field = String(r.field ?? '').trim();
  if (!field) return null;
  return { field, before: r.before ?? null, after: r.after ?? null };
}

function parseAuditLogItem(raw: unknown): AuditLogItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id)) return null;
  const productId = Number(r.productId ?? r.product_id ?? 0);
  const at = typeof r.at === 'string' && r.at ? r.at : new Date(0).toISOString();
  const changes = Array.isArray(r.changes)
    ? r.changes.map(parseChange).filter((c): c is AuditChange => c !== null)
    : [];
  const variantIdRaw = r.variantId ?? r.variant_id;
  const imageIdRaw = r.imageId ?? r.image_id;
  return {
    id,
    at,
    kind: String(r.kind ?? '').trim() || 'unknown',
    productId: Number.isFinite(productId) ? productId : 0,
    variantId: variantIdRaw != null ? Number(variantIdRaw) : null,
    imageId: imageIdRaw != null ? Number(imageIdRaw) : null,
    operation:
      String(r.operation ?? '')
        .trim()
        .toUpperCase() || 'UPDATE',
    entityId: r.entityId != null ? String(r.entityId) : null,
    changeCount: Number(r.changeCount ?? r.change_count ?? changes.length),
    changes,
    label: String(r.label ?? '').trim(),
  };
}

function parsePage(body: unknown, fallbackPage: number, fallbackPageSize: number): AuditLogPage {
  const empty: AuditLogPage = {
    items: [],
    total: 0,
    page: fallbackPage,
    pageSize: fallbackPageSize,
    order: 'desc',
  };
  if (!body || typeof body !== 'object') return empty;
  const o = body as Record<string, unknown>;
  const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
    string,
    unknown
  >;
  const rawItems = inner.items;
  const items = Array.isArray(rawItems)
    ? rawItems.map(parseAuditLogItem).filter((x): x is AuditLogItem => x !== null)
    : [];
  const total =
    typeof inner.total === 'number' && Number.isFinite(inner.total) ? inner.total : items.length;
  const page = typeof inner.page === 'number' && inner.page >= 1 ? inner.page : fallbackPage;
  const pageSize =
    typeof inner.pageSize === 'number' && inner.pageSize >= 1 ? inner.pageSize : fallbackPageSize;
  const orderRaw = String(inner.order ?? 'desc').toLowerCase();
  const order: AuditOrder = orderRaw === 'asc' ? 'asc' : 'desc';
  return { items, total, page, pageSize, order };
}

// ─── Exported functions ───────────────────────────────────────────────────────

export async function fetchProductAuditLog(
  productId: number,
  query: AuditLogQuery = {}
): Promise<AuditLogResult> {
  if (!Number.isFinite(productId) || productId <= 0) {
    return { ok: false, error: 'ID de producto inválido.' };
  }
  const page = query.page ?? 1;
  const pageSize = query.pageSize ?? 25;
  try {
    const { data } = await authClient.get<unknown>(`/api/admin/audit-logs/products/${productId}`, {
      params: { page, pageSize, order: query.order ?? 'desc' },
    });
    return { ok: true, data: parsePage(data, page, pageSize) };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404 ? 'Producto no encontrado.' : null) ??
      'No se pudo cargar el historial del producto.';
    return { ok: false, error: msg };
  }
}

export async function fetchVariantAuditLog(
  variantId: number,
  query: AuditLogQuery = {}
): Promise<AuditLogResult> {
  if (!Number.isFinite(variantId) || variantId <= 0) {
    return { ok: false, error: 'ID de variante inválido.' };
  }
  const page = query.page ?? 1;
  const pageSize = query.pageSize ?? 25;
  try {
    const { data } = await authClient.get<unknown>(`/api/admin/audit-logs/variants/${variantId}`, {
      params: { page, pageSize, order: query.order ?? 'desc' },
    });
    return { ok: true, data: parsePage(data, page, pageSize) };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404 ? 'Variante no encontrada.' : null) ??
      'No se pudo cargar el historial de la variante.';
    return { ok: false, error: msg };
  }
}
